import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { GenericTimelyAccountId, GenericUUID } from './models.generic';

extendZodWithOpenApi(z);

// ================================================
// ENUMS
// ================================================

export const ProjectSuggestionStatusEnums = ['pending', 'accepted', 'dismissed'] as const;
export type ProjectSuggestionStatus = (typeof ProjectSuggestionStatusEnums)[number];
export const ProjectSuggestionStatusSchema = z.enum(ProjectSuggestionStatusEnums).openapi({
  description: 'Current status of the project suggestion set',
  example: 'pending',
});

// ================================================
// SCHEMAS
// ================================================

export const ProjectSuggestionItemSchema = z
  .object({
    name: z.string().max(50).openapi({
      description: 'Project name',
      example: 'Product Development',
    }),
    description: z.string().max(250).openapi({
      description: 'Project description with tool/site/keyword hints',
      example:
        'Feature development, bug fixes, and sprint tasks. Tools: IDEs, version control. Keywords: PR, commit, deploy.',
    }),
  })
  .openapi({ title: 'ProjectSuggestionItem' });

export type ProjectSuggestionItem = z.infer<typeof ProjectSuggestionItemSchema>;

// ================================================
// WEBHOOK PAYLOAD
// ================================================

export const ProjectSuggestionsWebhookPayloadSchema = z
  .object({
    timely_account_id: z.number().int().openapi({
      description: 'Timely account ID',
      example: 123,
    }),
    event: z.literal('project_suggestions_completed').openapi({
      description: 'Event type',
    }),
    projects: z.array(ProjectSuggestionItemSchema).min(1).max(10).openapi({
      description: 'Array of project suggestions (5-8 typical)',
    }),
  })
  .openapi({ title: 'ProjectSuggestionsWebhookPayload' });

export type ProjectSuggestionsWebhookPayload = z.infer<
  typeof ProjectSuggestionsWebhookPayloadSchema
>;

// ================================================
// API RESPONSE
// ================================================

export const ProjectSuggestionSchema = z
  .object({
    id: GenericUUID.openapi({ description: 'Suggestion set ID' }),
    timely_account_id: GenericTimelyAccountId,
    suggestions: z.array(ProjectSuggestionItemSchema).openapi({
      description: 'Array of project suggestions',
    }),
    status: ProjectSuggestionStatusSchema,
    created_at: z.string().openapi({
      description: 'Creation timestamp',
      example: '2026-03-04T12:00:00Z',
    }),
    updated_at: z.string().nullable().openapi({
      description: 'Last update timestamp',
    }),
  })
  .openapi({ title: 'ProjectSuggestion' });

export type ProjectSuggestion = z.infer<typeof ProjectSuggestionSchema>;

// ================================================
// UPDATE
// ================================================

export const UpdateProjectSuggestionStatusSchema = z
  .object({
    status: z.enum(['accepted', 'dismissed']).openapi({
      description: 'New status for the suggestion set',
      example: 'accepted',
    }),
  })
  .openapi({ title: 'UpdateProjectSuggestionStatus' });

export type UpdateProjectSuggestionStatus = z.infer<typeof UpdateProjectSuggestionStatusSchema>;

// ================================================
// TRIGGER
// ================================================

export const TriggerProjectSuggestionsSchema = z
  .object({
    timely_account_id: z.number().int().openapi({
      description: 'Timely account ID',
      example: 123,
    }),
    industry: z.string().openapi({
      description: 'Industry of the account',
      example: 'Software company',
    }),
    use_case: z.string().openapi({
      description: 'Primary use case for time tracking',
      example: 'Creating accurate timesheets',
    }),
    company_name: z.string().openapi({
      description: 'Company name',
      example: 'Acme Corp',
    }),
    role: z.string().optional().openapi({
      description:
        "User's job title/role. Optional — when present, it is used to personalize the AI-generated project suggestions.",
      example: 'Product Manager',
    }),
  })
  .openapi({ title: 'TriggerProjectSuggestions' });

export type TriggerProjectSuggestions = z.infer<typeof TriggerProjectSuggestionsSchema>;
